'use client';
import Link from 'next/link';
import dayjs from 'dayjs';
import Image from 'next/image';
import { useEffect, useMemo, useState } from 'react';
import { useSearchParams, useParams } from 'next/navigation';
import { setMobileCookie } from '@/stores';
import { useAuthMe } from '@/features/user';
import CustomImage from '@/components/ui/CustomImage';
import { AuthTokenStorageService } from '@/lib/auth';
import { useFooterData } from '@/lib/api';
import FeedbackDialog from '@/components/common/feedback/FeedbackDialog';
import Modal from '@/components/ui/Modal';
import Button from '@/components/ui/Button';
import FooterStoreBadgeLinks from '@/components/common/app-download/FooterStoreBadgeLinks';
import { FooterDataItem } from '@/shared';

export default function Footer() {
  const { url, slug } = useParams<{ url?: string; slug?: string }>();
  const [hasParam, setHasParam] = useState(false);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isHydrated, setIsHydrated] = useState(false);
  const hrSiteUrl =
    process.env.NEXT_PUBLIC_APP_HR_SITE_URL || 'https://hrms.zangia.mn';

  useEffect(() => {
    setIsHydrated(true);
    setHasParam(!!url || !!slug);
  }, [url, slug]);

  const { data: user } = useAuthMe();

  const hasOwnedCompanies =
    isHydrated && (user?.ownedCompanies?.length ?? 0) > 0;

  const { data } = useFooterData();
  const links = useMemo(
    () => [
      { href: '/page/policy', title: 'Үйлчилгээний нөхцөл' },
      { href: '/page/advertise', title: 'Сурталчилгаа байршуулах' },
      {
        href: hasOwnedCompanies ? `${hrSiteUrl}/pricing` : '/prices',
        title: 'Үнийн санал',
      },
      { href: '/page/aboutus', title: 'Бидний тухай' },
    ],
    [hrSiteUrl, hasOwnedCompanies]
  );
  const footer: FooterDataItem[] = data || [];
  const socials: Record<string, string> = useMemo(() => {
    try {
      return JSON.parse(footer.find((e) => e.type === 'social')?.value ?? '{}');
    } catch {
      return {} as Record<string, string>;
    }
  }, [footer]);

  const [isMobile, setIsMobile] = useState(false);
    const searchParams = useSearchParams();

  useEffect(() => {
    const fromQuery = searchParams.get('isMobile') === '1';
    if (fromQuery) {
      setMobileCookie(true);
    }
    if (fromQuery || AuthTokenStorageService.checkIsMobile()) {
      setIsMobile(true);
    }
  }, [searchParams]);

  if (isMobile) {
    return null;
  }

  return (
    <footer className="bg-[#F5F5F7] mt-10 w-full pb-5">
      <div className="container mx-auto pt-5 px-4">
        <div className="border-b-[1px] border-[#E1DFDD] pb-4">
          <Image
            src="/images/logos/logo.png"
            alt="Logo"
            width={185}
            height={30}
            style={{ height: '30px', width: 'auto' }}
          />
        </div>

        <div className="mt-5 flex flex-col sm:flex-row justify-start lg:justify-between gap-6 sm:gap-8">
          {!hasParam ? (
            <>
              <div className="text-left">
                <p>
                  <span className="font-medium text-[#4A4A4B]">Хаяг:</span>{' '}
                  <span className="font-normal text-[#474D6A]">
                    {footer?.find((e) => e.type == 'address')?.value}
                  </span>
                </p>
                <p>
                  <span className="font-medium text-[#4A4A4B]">Тусламж:</span>{' '}
                  <span className="font-normal text-[#474D6A]">
                    {(() => {
                      const value = footer?.find(
                        (e) => e.type === 'help_phones'
                      )?.value;
                      if (!value) return '-';
                      return value
                        .split(',')
                        .filter((e) => (e ?? '').length > 0)
                        .map((num) => num.slice(0, 4) + '-' + num.slice(4))
                        .join(', ');
                    })()}
                  </span>
                </p>
                <p>
                  <span className="font-medium text-[#4A4A4B]">И-мэйл:</span>{' '}
                  <span className="font-normal text-[#474D6A]">
                    {footer?.find((e) => e.type == 'email')?.value}
                  </span>
                </p>

                <div className="flex justify-center sm:justify-start gap-2.5 mt-4">
                  {['fb', 'ig', 'tw', 'yt'].map((e, ind) => {
                    if (socials?.[e]) {
                      return (
                        <Link key={ind} href={socials?.[e]} prefetch={false}>
                          <Image
                            src={`/images/footer/${e}.svg`}
                            alt={e}
                            width={24}
                            height={24}
                            style={{ height: '24px', width: 'auto' }}
                          />
                        </Link>
                      );
                    }
                  })}
                </div>
              </div>
              <div className="text-left">
                <ul className="flex flex-col gap-2">
                  {links.map((link, index) => (
                    <li key={index}>
                      <Link
                        href={link.href}
                        className="font-medium text-sm text-[#4A4A4B] uppercase hover:underline"
                        prefetch={false}
                      >
                        {link.title}
                      </Link>
                    </li>
                  ))}

                  <button
                    className="self-start font-medium text-sm text-[#4A4A4B] uppercase hover:underline text-left"
                    onClick={() => {
                      setIsDialogOpen(true);
                    }}
                  >
                    Санал хүсэлт
                  </button>
                </ul>
              </div>
              <div className="hidden lg:flex flex-col items-center gap-2.5">
                <FooterStoreBadgeLinks />
                <Image
                  src="/images/footer/footer-qr.png"
                  alt="QR Code"
                  width={120}
                  height={120}
                  style={{ height: '120px', width: 'auto' }}
                />
              </div>
            </>
          ) : (
            <div className="w-full hidden lg:flex items-start gap-2.5 justify-between">
              <div className="text-left">
                <ul className="flex gap-5">
                  {links.map((link, index) => (
                    <li key={index}>
                      <Link
                        href={link.href}
                        className="font-medium text-sm text-[#4A4A4B] uppercase hover:underline"
                      >
                        {link.title}
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
              <div className="flex gap-5">
                <FooterStoreBadgeLinks className="flex flex-col items-center gap-5" />
                <CustomImage
                  src="/images/footer/footer-qr.png"
                  alt="QR Code"
                  width={90}
                  height={90}
                />
              </div>
            </div>
          )}
        </div>

        <div className="text-center mt-5 p-2.5 text-sm">
          <p className="font-normal text-[#717B9E]">
            Copyright &copy; 2008 - {dayjs().format('YYYY')}
          </p>
        </div>
      </div>

      <FeedbackDialog
        isOpen={isDialogOpen}
        onClose={() => {
          setIsDialogOpen(false);
        }}
        onSuccess={() => setIsModalOpen(true)}
      />
      <Modal
        isOpen={isModalOpen}
        setIsOpen={() => setIsModalOpen(false)}
        title={''}
        footer={null}
        header={false}
      >
        <div className="relative h-full bg-white pt-20 pb-10 flex flex-col gap-5">
          <div className="absolute top-0 left-1/2 -translate-x-1/2 z-10 bg-green-500 px-4 py-2 rounded-b-lg flex items-center justify-center">
            <div className="flex gap-2 items-center">
              <p className="text-white text-sm font-semibold whitespace-nowrap">
                Амжилттай
              </p>
            </div>
          </div>
          <div className="flex justify-center items-center">
            <div className="flex justify-center items-center w-32 h-32 rounded-full bg-green-100">
              <Image
                src={`/icons/icon-check-green.svg`}
                alt=""
                width={80}
                height={80}
              />
            </div>
          </div>
          <div className="text-center justify-center items-center font-normal text-sm text-[#000000A3]">
            <p>
              Таны хүсэлтийг хүлээн авлаа. <br /> Баярлалаа!
            </p>
          </div>
          <Button
            variant={'primary'}
            className="px-[131px] py-3 text-sm rounded-lg h-10 bg-primary self-center"
            onClick={() => setIsModalOpen(false)}
          >
            OK
          </Button>
        </div>
      </Modal>
    </footer>
  );
}
